import last from 'lodash/last';
import type {
  ActiveSeasonPassWithoutPremium,
  ConditionHandlersObjects,
  HS774Distribution,
  Payer,
  Tier1Country,
} from './conditionsTypes';
import { ConditionType, simpleAmountConditionHandler } from './conditionsTypes';
import type { OffersGameData, StateProxy } from './offersTypes';
import { MS_IN_DAY, MS_IN_HOUR } from '../../utils/time-utils';
import { InventoryItem } from '../resources/types';
import MathUtils from '../../utils/MathUtils';
import type { ConditionTestProps } from '../../libs/offerSystem/offerSystemTypes';
import { getHS774Distribution } from '../splitTest/tests/hs774/HS-774-seasonPass';

const standardEditorProps = {
  operator: ['GREATER OR EQUAL', 'GREATER', 'EQUAL', 'LESS', 'LESS OR EQUAL'],
};

export const conditions: ConditionHandlersObjects<StateProxy, OffersGameData> = {
  [ConditionType.Shown]: {
    createNew() {
      return {
        type: ConditionType.Shown,
        productId: '',
        operator: 'EQUAL',
        value: 0,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.productId} ${condition.operator} ${condition.value}`;
    },
    testCondition({ condition, purchaseHistory, offer }) {
      return simpleAmountConditionHandler(
        condition,
        purchaseHistory.getShownAmount(condition.productId === '' ? offer.id : condition.productId),
      );
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.Bought]: {
    createNew() {
      return {
        type: ConditionType.Bought,
        productId: '',
        operator: 'EQUAL',
        value: 0,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.productId} ${condition.operator} ${condition.value}`;
    },
    testCondition({ condition, purchaseHistory, offer }) {
      return simpleAmountConditionHandler(
        condition,
        purchaseHistory.getBoughtAmount(condition.productId === '' ? offer.id : condition.productId),
      );
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.BoughtInstanceLimit]: {
    createNew() {
      return {
        type: ConditionType.BoughtInstanceLimit,
        operator: 'EQUAL',
        value: 1,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      if (!props.activationId) return false;
      return simpleAmountConditionHandler(
        props.condition,
        props.purchaseHistory.getInstanceBoughtAmount(props.offer.id, props.activationId),
      );
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.BoughtCooldownHours]: {
    createNew() {
      return {
        type: ConditionType.BoughtCooldownHours,
        operator: 'EQUAL',
        value: 1,
        productId: '',
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.productId} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      const lastTimeBought = props.purchaseHistory.getLastTimeBought(props.offer.id);
      if (!lastTimeBought) return true;
      return simpleAmountConditionHandler(
        props.condition,
        (props.state.getCurrentTime() - lastTimeBought) / MS_IN_HOUR,
      );
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.ShownCooldownHours]: {
    createNew() {
      return {
        type: ConditionType.ShownCooldownHours,
        operator: 'EQUAL',
        passOnNotShown: false,
        value: 1,
        productId: '',
      };
    },
    toString(condition) {
      return `${condition.type}  ${condition.productId} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      const lastDeactivation = props.purchaseHistory.getLastDeactivationTime(props.condition.productId);
      if (!lastDeactivation) {
        return props.condition.passOnNotShown;
      }
      return simpleAmountConditionHandler(
        props.condition,
        (props.state.getCurrentTime() - lastDeactivation) / MS_IN_HOUR,
      );
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.Band]: {
    createNew() {
      return {
        type: ConditionType.Band,
        operator: 'EQUAL',
        value: 1,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      return simpleAmountConditionHandler(
        props.condition,
        props.state.getBand(props.purchaseHistory.getAllPurchases()),
      );
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.ActiveDays]: {
    createNew() {
      return {
        type: ConditionType.ActiveDays,
        operator: 'EQUAL',
        value: 1,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      return simpleAmountConditionHandler(
        props.condition,
        (props.state.getCurrentTime() - props.state.getUserCreateDate()) / MS_IN_DAY,
      );
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.DaysSinceLastPurchase]: {
    createNew() {
      return {
        type: ConditionType.DaysSinceLastPurchase,
        operator: 'EQUAL',
        strict: true,
        value: 1,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      const purchases = props.purchaseHistory.getAllPurchases();
      purchases.sort((a, b) => a.date - b.date);
      const lastPurchase = last(purchases) ?? null;

      if (!lastPurchase) return !props.condition.strict;
      const daysSinceLastPurchase = Math.floor((props.state.getCurrentTime() - lastPurchase.date) / MS_IN_DAY);
      return simpleAmountConditionHandler(props.condition, daysSinceLastPurchase);
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.Level]: {
    createNew() {
      return {
        type: ConditionType.Level,
        operator: 'EQUAL',
        value: 1,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      return simpleAmountConditionHandler(props.condition, props.state.getLevelNumber());
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.InventoryBalance]: {
    createNew() {
      return {
        type: ConditionType.InventoryBalance,
        inventoryType: InventoryItem.Coin,
        operator: 'EQUAL',
        value: 50,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.inventoryType} ${condition.operator} ${condition.value}`;
    },
    testCondition({ condition, state }) {
      const inventoryAmount = state.getInventory()[condition.inventoryType] ?? 0;
      return simpleAmountConditionHandler(condition, inventoryAmount);
    },
    editorPropsTypes: { ...standardEditorProps, inventoryType: Object.values(InventoryItem) },
  },
  [ConditionType.Time]: {
    createNew() {
      return {
        type: ConditionType.Time,
        operator: 'EQUAL',
        value: Date.now(),
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      return simpleAmountConditionHandler(props.condition, props.state.getCurrentTime());
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.BoughtTag]: {
    createNew() {
      return {
        type: ConditionType.BoughtTag,
        operator: 'EQUAL',
        tag: '',
        value: 0,
      };
    },
    toString(condition) {
      return `Bought 🏷${condition.tag} ${condition.operator} ${condition.value}`;
    },
    testCondition(props): boolean {
      const taggedOffers = props.allOffers.filter((offer) => offer.tags.includes(props.condition.tag));
      if (taggedOffers.length === 0) {
        console.warn(`No offers with tag ${props.condition.tag}`);
        return false;
      }
      const amountPurchased = taggedOffers.reduce(
        (acc, offer) => acc + props.purchaseHistory.getBoughtAmount(offer.id),
        0,
      );
      return simpleAmountConditionHandler(props.condition, amountPurchased);
    },
  },
  [ConditionType.ShownCooldownTag]: {
    createNew() {
      return {
        type: ConditionType.ShownCooldownTag,
        operator: 'EQUAL',
        passOnNotShown: false,
        tag: '',
        value: 0,
      };
    },
    toString(condition) {
      return `Shown cooldown 🏷${condition.tag} ${condition.operator} ${condition.value} (${condition.passOnNotShown ? 'Loose' : 'Strict'})`;
    },
    testCondition(props): boolean {
      const taggedOffers = props.allOffers.filter((offer) => offer.tags.includes(props.condition.tag));
      if (taggedOffers.length === 0) {
        console.warn(`No offers with tag ${props.condition.tag}`);
        return false;
      }

      const activeOffersIds = props.purchaseHistory.getActiveOffers().map(({ offer }) => offer.id);

      // if any offer is active fail
      if (taggedOffers.some((offer) => activeOffersIds.includes(offer.id))) {
        return false;
      }

      const lastDeactivation = taggedOffers.reduce(
        (acc, offer) => Math.max(acc, props.purchaseHistory.getLastDeactivationTime(offer.id) ?? 0),
        0,
      );

      if (lastDeactivation === 0) {
        return props.condition.passOnNotShown;
      }

      return simpleAmountConditionHandler(
        props.condition,
        (props.state.getCurrentTime() - lastDeactivation) / MS_IN_HOUR,
      );
    },
  },
  [ConditionType.Trigger]: {
    createNew() {
      return {
        type: ConditionType.Trigger,
        trigger: '',
      };
    },
    toString(condition) {
      return `Trigger 🏷${condition.trigger}`;
    },
    testCondition(props): boolean {
      return props.trigger === props.condition.trigger;
    },
  },
  [ConditionType.PurchaseCooldownHours]: {
    createNew() {
      return {
        type: ConditionType.PurchaseCooldownHours,
        passOnNoPurchase: true,
        productId: '',
        operator: 'GREATER OR EQUAL',
        value: 1,
      };
    },
    toString(condition) {
      return `Purchase cooldown 🏷${condition.productId} ${condition.operator} ${condition.value} (${condition.passOnNoPurchase ? 'Loose' : 'Strict'})`;
    },
    testCondition(props): boolean {
      const purchases = props.purchaseHistory
        .getAllPurchases()
        .filter(({ offerId }) => offerId === props.condition.productId);

      if (purchases.length === 0) {
        return props.condition.passOnNoPurchase;
      }

      const lastPurchaseDate = purchases.reduce((acc, { date }) => Math.max(acc, date), 0);

      const hoursPassed = (props.state.getCurrentTime() - lastPurchaseDate) / MS_IN_HOUR;

      return simpleAmountConditionHandler(props.condition, hoursPassed);
    },
  },
  [ConditionType.LegacyBand]: {
    createNew() {
      return {
        type: ConditionType.LegacyBand,
        operator: 'EQUAL',
        value: 1,
      };
    },
    toString(condition) {
      return `${condition.type} ${condition.operator} ${condition.value}`;
    },
    testCondition(props) {
      const purchaseExpirationInHours = 2160;
      const purchasesToConsider = props.purchaseHistory
        .getAllPurchases()
        // SEEMS LIKE A BUG to divide on one side and multiply on other
        .filter((entry) => (Date.now() - entry.date) / MS_IN_HOUR < purchaseExpirationInHours * MS_IN_HOUR);

      const oldUpsellBundlesBands = [
        { min: 0, max: 0 },
        { min: 1, max: 500 },
        { min: 501, max: 1000 },
        { min: 1001, max: 2000 },
        { min: 2001, max: 3000 },
        { min: 3001, max: 5000 },
        { min: 5001, max: Infinity },
      ];

      const highestPurchase =
        purchasesToConsider.length > 0
          ? purchasesToConsider.reduce((a, b) => (a.usdCentPrice > b.usdCentPrice ? a : b))?.usdCentPrice
          : 0;

      // ANOTHER BUG - condition for findIndex is incorrect, so it chooses tier 1 higher than it should and wraps around in case of the highest one
      const band =
        MathUtils.clamp(
          oldUpsellBundlesBands.findIndex((range) => range.min >= highestPurchase && highestPurchase <= range.max),
          0,
          oldUpsellBundlesBands.length - 1,
        ) + 1;

      return simpleAmountConditionHandler(props.condition, band);
    },
    editorPropsTypes: standardEditorProps,
  },
  [ConditionType.Tier1Country]: {
    createNew() {
      return {
        type: ConditionType.Tier1Country,
        value: true,
      };
    },
    toString(condition) {
      return condition.value ? 'Is Tier 1 country' : 'Is ROW';
    },
    testCondition(props: ConditionTestProps<Tier1Country, StateProxy, OffersGameData>): boolean {
      return props.state.isTier1() === props.condition.value;
    },
  },
  [ConditionType.HS774Distribution]: {
    createNew(): HS774Distribution {
      return {
        type: ConditionType.HS774Distribution,
        distribution: 1,
      };
    },
    toString(condition) {
      return `${condition.type} is equal ${condition.distribution}`;
    },
    testCondition(props: ConditionTestProps<HS774Distribution, StateProxy, OffersGameData>): boolean {
      return getHS774Distribution() === props.condition.distribution;
    },
  },
  [ConditionType.ActiveSeasonPassWithoutPremium]: {
    createNew(): ActiveSeasonPassWithoutPremium {
      return {
        type: ConditionType.ActiveSeasonPassWithoutPremium,
        value: true,
      };
    },
    toString(condition) {
      return `Season pass is ${condition.value ? '' : 'NOT'} active`;
    },
    testCondition(props: ConditionTestProps<ActiveSeasonPassWithoutPremium, StateProxy, OffersGameData>): boolean {
      return props.condition.value === props.state.isInSeasonPassWithoutPremium();
    },
  },
  [ConditionType.Payer]: {
    createNew(): Payer {
      return {
        type: ConditionType.Payer,
        value: false,
      };
    },
    toString(condition) {
      return `Player is ${condition.value ? 'PAYER' : 'NON PAYER'}`;
    },
    testCondition(props: ConditionTestProps<Payer, StateProxy, OffersGameData>): boolean {
      return props.condition.value === props.purchaseHistory.getAllPurchases().length > 0;
    },
  },
};
